import { PlusIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import Checkbox from "../../../basic/checkbox/Checkbox";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import SortSelect from "../../../basic/sortSelect/SortSelect";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import DottedNameList from "./DottedNameList";

export const MAX_SELECTED_COLUMNS = 2;
const PREVIEW_VALUE_COUNT = 8;

interface ColumnOption {
    name: string;
    value: string;
}

interface ColumnSelectionStepProps {
    fileName: string;
    hasHeader: boolean;
    dataRows: string[][];
    columnCount: number;
    columnOptions: ColumnOption[];
    selectedColumnIndices: number[];
    onToggleHasHeader: () => void;
    onSelectColumn: (position: number, value: string) => void;
    onAddColumn: () => void;
    onRemoveColumn: (position: number) => void;
    onPickDifferentFile: () => void;
}

const ColumnSelectionStep = (props: ColumnSelectionStepProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t_fileName = t("File name:", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_firstRowHasTitles = t("First row contains column titles", {
        _tags: "SchoolClasses, UploadStudentListPopup",
    });
    const t_column = t("Column", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_columnWithNumber = (columnNumber: number) =>
        t("Column {columnNumber}:", { _tags: "SchoolClasses, UploadStudentListPopup", columnNumber });
    const t_addColumn = t("Add column", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_sr_removeChip = t("Remove", { _tags: "SchoolClasses, UploadStudentListPopup, ScreenReaderOnly" });
    const t_sr_changeFile = t("Choose a different file", {
        _tags: "SchoolClasses, UploadStudentListPopup, ScreenReaderOnly",
    });

    const renderColumnPreview = (columnIndex: number) => {
        const previewValues = props.dataRows
            .map((row) => row[columnIndex])
            .filter(Boolean)
            .slice(0, PREVIEW_VALUE_COUNT);
        return (
            <DottedNameList
                names={previewValues}
                textColorClass="text-p6-grey-mid"
                dotColorClass="bg-p6-grey-mid"
            />
        );
    };

    // A column that another dropdown already uses stays visible but unselectable, so the same
    // column can never end up in both dropdowns.
    const getOptionsForPosition = (position: number) =>
        props.columnOptions.map((option) => ({
            ...option,
            disabled:
                Number(option.value) !== props.selectedColumnIndices[position] &&
                props.selectedColumnIndices.includes(Number(option.value)),
        }));

    const renderColumnRow = (position: number) => {
        const showsMultipleColumns = props.selectedColumnIndices.length > 1;
        return (
            <div
                key={position}
                className="flex flex-col gap-xxs"
            >
                <div
                    className={`flex ${getStyleBasedOnBreakpoint(
                        Breakpoint.SM,
                        "items-center gap-s",
                        "flex-col gap-xxs"
                    )}`}
                >
                    <span
                        className={`text-l text-p6-grey-dark font-semibold ${getStyleBasedOnBreakpoint(
                            Breakpoint.SM,
                            "shrink-0 min-w-[80px]"
                        )}`}
                    >
                        {showsMultipleColumns ? t_columnWithNumber(position + 1) : `${t_column}:`}
                    </span>
                    <div className="flex flex-1 min-w-no gap-s items-center">
                        <div className="flex-1 min-w-no">
                            <SortSelect
                                options={getOptionsForPosition(position)}
                                value={String(props.selectedColumnIndices[position])}
                                onChange={(value) => props.onSelectColumn(position, value)}
                            />
                        </div>
                        {showsMultipleColumns && (
                            <button
                                type="button"
                                aria-label={`${t_sr_removeChip} ${t_column} ${position + 1}`}
                                onClick={() => props.onRemoveColumn(position)}
                                className="flex items-center justify-center bg-transparent border-none cursor-pointer p-no shrink-0"
                            >
                                <PhaseSixIcon
                                    name="trash"
                                    className="text-xl text-p6-grey-dark"
                                />
                            </button>
                        )}
                    </div>
                </div>
                {renderColumnPreview(props.selectedColumnIndices[position])}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-m">
            <div className="flex flex-col gap-xxs">
                <div
                    className={`flex ${getStyleBasedOnBreakpoint(
                        Breakpoint.SM,
                        "items-center gap-xs",
                        "flex-col gap-xxs"
                    )}`}
                >
                    <span className="text-l text-p6-grey-dark font-semibold shrink-0">{t_fileName}</span>
                    <div className="flex gap-xs items-center min-w-no">
                        <span className="text-l text-p6-grey-dark min-w-no truncate">{props.fileName}</span>
                        <button
                            type="button"
                            aria-label={t_sr_changeFile}
                            onClick={props.onPickDifferentFile}
                            className="flex items-center justify-center bg-transparent border-none cursor-pointer p-no shrink-0"
                        >
                            <PhaseSixIcon
                                name="edit-avatar"
                                className="text-xl text-p6-grey-dark"
                            />
                        </button>
                    </div>
                </div>
                <div className="flex gap-xs items-center min-h-[40px]">
                    <Checkbox
                        isChecked={props.hasHeader}
                        onClick={props.onToggleHasHeader}
                        ariaLabel={t_firstRowHasTitles}
                    />
                    <span
                        className="text-m text-p6-grey-dark cursor-pointer"
                        onClick={props.onToggleHasHeader}
                    >
                        {t_firstRowHasTitles}
                    </span>
                </div>
            </div>
            <div className="border-t border-p6-grey-lightest" />
            {props.selectedColumnIndices.map((_, position) => renderColumnRow(position))}
            {props.selectedColumnIndices.length < MAX_SELECTED_COLUMNS &&
                props.selectedColumnIndices.length < props.columnCount && (
                    <button
                        type="button"
                        onClick={props.onAddColumn}
                        className="flex gap-xs items-center self-start bg-transparent border-none cursor-pointer p-no text-l text-p6-grey-dark hover:text-p6-orange"
                    >
                        <PhosphorIcon
                            phosphorIcon={PlusIcon}
                            fontSize="text-xl"
                        />
                        {t_addColumn}
                    </button>
                )}
        </div>
    );
};

export default ColumnSelectionStep;
